"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type EnergyUnit,
  type EnthalpyResultStrings,
  EnthalpyResults,
  molesFrom,
  PerSpeciesControls,
} from "@/components/tools/chem-reference/enthalpy-results";
import {
  EnthalpyTable,
  type EnthalpyTableStrings,
} from "@/components/tools/chem-reference/enthalpy-table";
import {
  HessPanel,
  type HessStrings,
  type StepState,
} from "@/components/tools/chem-reference/hess-steps";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  appendSpecies,
  balanceReport,
  parseEquation,
  REACTION_PRESETS,
  reactionEnthalpy,
  type TableSpecies,
  type TermProblem,
} from "@/lib/tool-kit/chem-reference/reaction-enthalpy";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const ENTHALPY_OF_REACTION_CALCULATOR_STRINGS = {
  modeLabel: "Method",
  modeFormation: "From enthalpies of formation",
  modeHess: "Combine reactions (Hess's law)",
  equationLabel: "Reaction equation",
  equationHint:
    "Balanced, with states: CH4(g) + 2 O2(g) -> CO2(g) + 2 H2O(l). Put spaces around +, write charges as Na+ or SO4^2-, and add @value for a species not in the table, such as C8H18(l)@-250.1.",
  equationPlaceholder: "CH4(g) + 2 O2(g) -> CO2(g) + 2 H2O(l)",
  presetLabel: "Try a reaction",
  presetNames: {
    methane: "Methane burning",
    glucose: "Glucose burning",
    ammonia: "Ammonia synthesis",
    limestone: "Limestone decomposing",
    thermite: "Thermite",
    neutralisation: "Neutralisation",
  },
  unitLabel: "Show ΔH in",
  unitNames: { kJ: "kJ", kcal: "kcal" },
  results: "Results",
  enterPrompt: "Type a reaction with an arrow to calculate ΔH°.",
  noArrow: "Add an arrow between reactants and products: ->, → or =.",
  problemsHeading: "These entries need attention:",
  problemMessages: {
    syntax: "“{token}” could not be read as a species.",
    formula: "“{token}” is not a valid chemical formula.",
    unknown:
      "“{token}” is not in the table. Add its ΔfH° yourself with @, for example {token}@-250.1, or choose another state.",
    ambiguous:
      "“{token}” exists in more than one state ({states}). Add the state, such as (l) or (g).",
    coefficient: "“{token}” has a coefficient outside 0–1000.",
  },
  needBothSides: "Both sides of the equation need at least one species.",
  deltaH: "ΔH°rxn",
  heat: "Heat flow",
  heatNames: {
    exothermic: "Exothermic (ΔH < 0)",
    endothermic: "Endothermic (ΔH > 0)",
    none: "No net change",
  },
  productSum: "Σ ΔfH° of products",
  reactantSum: "Σ ΔfH° of reactants",
  columnSide: "Side",
  columnCoefficient: "Coefficient",
  columnSpecies: "Species",
  columnName: "Name",
  columnDhf: "ΔfH° (kJ/mol)",
  columnContribution: "Coefficient × ΔfH° (kJ)",
  sideReactant: "Reactant",
  sideProduct: "Product",
  customMark: "*",
  customNote: "* marks a ΔfH° you supplied with @; it is not from the table.",
  working: "The working",
  workProducts: "Σ products = {sum} = {total}",
  workReactants: "Σ reactants = {sum} = {total}",
  workDelta:
    "ΔH°rxn = Σ products − Σ reactants = {products} − ({reactants}) = {delta}",
  unbalanced:
    "The equation is not balanced ({items} differ). The result is for the equation as typed; balance it first for a meaningful answer.",
  perLabel: "Express the heat for",
  perReaction: "The reaction as written",
  perSpecies: "A given amount of {species}",
  amountLabel: "Amount",
  amountUnitLabel: "Unit",
  amountUnits: { mol: "mol", g: "g" },
  heatReleased: "Heat released by {species}",
  heatAbsorbed: "Heat absorbed by {species}",
  copy: "Copy result",
  copied: "Copied!",
  copyText: "ΔH°rxn = {delta} ({heat})",
  tableHeading: "Standard enthalpies of formation at 298.15 K",
  filterLabel: "Filter the table",
  filterPlaceholder: "water, CO2, (g), Fe",
  columnAdd: "Add to the equation",
  addReactant: "+ reactant",
  addProduct: "+ product",
  addReactantLabel: "Add {species} to the reactants",
  addProductLabel: "Add {species} to the products",
  noSpecies: "No species match that filter.",
  tableSource:
    "Source: CRC Handbook of Chemistry and Physics, 84th ed., as tabulated in LibreTexts “T1: Standard Thermodynamic Quantities”. Other handbooks differ in the last digit or two.",
  stepsHeading: "Steps to combine",
  stepDelta: "ΔH of step {n}",
  stepMultiplier: "Multiply step {n} by",
  stepReverse: "Reverse step {n}",
  stepRemove: "Remove step {n}",
  addStep: "Add a step",
  hessHint:
    "Enter the ΔH of each known reaction, in one unit. Reversing a reaction flips the sign of its ΔH, and multiplying a reaction multiplies it. A negative multiplier also reverses a step.",
  hessEmpty: "Enter the ΔH of at least one step.",
  hessIncomplete:
    "Steps with a missing or unreadable ΔH or multiplier are left out.",
  hessTotal: "ΔH of the target reaction",
  hessColumnStep: "Step",
  hessColumnDelta: "ΔH as written",
  hessColumnFactor: "Change",
  hessColumnContribution: "Contribution",
  hessFactorReversed: "reversed",
  hessWorking: "The working",
  hessWorkLine: "ΔH = {terms}",
  hessWorkTotal: "ΔH = {total}",
  hessUnit: "kJ",
  hessPreset: "Example: C(s) + ½ O₂ → CO from two combustions",
} as const;

type Strings = typeof ENTHALPY_OF_REACTION_CALCULATOR_STRINGS;
type Mode = "formation" | "hess";

const UNITS: readonly EnergyUnit[] = ["kJ", "kcal"];

const HESS_PRESET: StepState[] = [
  { id: 1, delta: "-393.5", multiplier: "1", reversed: false },
  { id: 2, delta: "-283.0", multiplier: "1", reversed: true },
];

function problemText(strings: Strings, problem: TermProblem): string {
  const states =
    problem.kind === "ambiguous"
      ? problem.states.map((s) => `(${s})`).join(", ")
      : "";
  return t(strings.problemMessages[problem.kind], {
    token: problem.token.trim(),
    states,
  });
}

function FormationPanel({ strings }: { strings: Strings }) {
  const fmt = useToolNumber();
  const [text, setText] = useState(REACTION_PRESETS[0].equation);
  const [unit, setUnit] = useState<EnergyUnit>("kJ");
  const [per, setPer] = useState("reaction");
  const [amount, setAmount] = useState("10");
  const [amountUnit, setAmountUnit] = useState<"mol" | "g">("g");

  const equation = parseEquation(text);
  const problems = equation
    ? [...equation.reactants.problems, ...equation.products.problems]
    : [];
  const result = equation ? reactionEnthalpy(equation) : null;
  const terms = equation
    ? [...equation.reactants.terms, ...equation.products.terms]
    : [];
  const perTerm = per === "reaction" ? null : (terms[Number(per)] ?? null);
  const amountValue = parseMeasuredNumber(amount);
  const moles =
    perTerm && amountValue !== null && amountValue > 0
      ? molesFrom(perTerm, amountValue, amountUnit)
      : null;

  function add(species: TableSpecies, side: "reactants" | "products") {
    setText(appendSpecies(text, side, species));
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.presetLabel}</span>
        {REACTION_PRESETS.map((preset) => (
          <button
            className={BUTTON_SMALL}
            key={preset.id}
            onClick={() => {
              setText(preset.equation);
              setPer("reaction");
            }}
            type="button"
          >
            {strings.presetNames[preset.id as keyof typeof strings.presetNames]}
          </button>
        ))}
      </div>
      <TextAreaField
        hint={strings.equationHint}
        id="er-equation"
        label={strings.equationLabel}
        onChange={setText}
        placeholder={strings.equationPlaceholder}
        rows={2}
        value={text}
      />
      <PillGroup
        label={strings.unitLabel}
        name="er-unit"
        onChange={setUnit}
        options={UNITS.map((value) => ({
          value,
          label: strings.unitNames[value],
        }))}
        value={unit}
      />
      {result ? (
        <PerSpeciesControls
          amount={amount}
          amountUnit={amountUnit}
          onAmount={setAmount}
          onAmountUnit={setAmountUnit}
          onSelect={setPer}
          selected={per}
          strings={strings as EnthalpyResultStrings}
          terms={terms}
        />
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {text.trim() === "" ? <Notice>{strings.enterPrompt}</Notice> : null}
        {text.trim() !== "" && !equation ? (
          <ErrorNotice>{strings.noArrow}</ErrorNotice>
        ) : null}
        {problems.length > 0 ? (
          <ErrorNotice>
            {strings.problemsHeading}{" "}
            {problems.map((p) => problemText(strings, p)).join(" ")}
          </ErrorNotice>
        ) : null}
        {equation &&
        problems.length === 0 &&
        (equation.reactants.terms.length === 0 ||
          equation.products.terms.length === 0) ? (
          <Notice>{strings.needBothSides}</Notice>
        ) : null}
        {equation && result ? (
          <EnthalpyResults
            balance={balanceReport(equation)}
            fmt={fmt}
            heatMoles={moles}
            heatTerm={perTerm}
            result={result}
            strings={strings as EnthalpyResultStrings}
            unit={unit}
          />
        ) : null}
      </section>
      <EnthalpyTable
        fmt={fmt}
        onAdd={add}
        strings={strings as EnthalpyTableStrings}
      />
    </>
  );
}

function HessSection({ strings }: { strings: Strings }) {
  const fmt = useToolNumber();
  const [steps, setSteps] = useState<StepState[]>(HESS_PRESET);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSteps(HESS_PRESET)}
          type="button"
        >
          {strings.hessPreset}
        </button>
      </div>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <HessPanel
        fmt={fmt}
        onChange={setSteps}
        steps={steps}
        strings={strings as HessStrings}
      />
    </>
  );
}

export default function EnthalpyOfReactionCalculatorWidget() {
  const strings = useToolUi(ENTHALPY_OF_REACTION_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("formation");
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="er-mode"
        onChange={setMode}
        options={[
          { value: "formation", label: strings.modeFormation },
          { value: "hess", label: strings.modeHess },
        ]}
        value={mode}
      />
      {mode === "formation" ? (
        <FormationPanel strings={strings} />
      ) : (
        <HessSection strings={strings} />
      )}
    </>
  );
}
